import { useSearch } from '@tanstack/react-router'
import { useState } from 'react'
import { ApiError } from '../api/client'
import { useSignIn } from '../api/hooks'
import { Wordmark } from '../primitives'

const formatLockoutTime = (iso: string): string => {
  const d = new Date(iso)
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

export const SignInScreen = () => {
  const search = useSearch({ strict: false }) as { next?: string }
  const signIn = useSignIn()
  const [handle, setHandle] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [lockoutUntil, setLockoutUntil] = useState<string | null>(null)

  const onSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault()
    setError(null)
    setLockoutUntil(null)
    const trimmedHandle = handle.trim().replace(/^@/, '')
    const trimmedCode = code.trim()
    if (!trimmedHandle || !trimmedCode) {
      setError('Enter your handle and the code kwb sent you.')
      return
    }
    try {
      await signIn.mutateAsync({ handle: trimmedHandle, code: trimmedCode })
      const next = typeof search.next === 'string' ? decodeURIComponent(search.next) : '/home'
      window.location.href = next
    } catch (err) {
      if (err instanceof ApiError) {
        if (
          err.status === 429 &&
          err.body &&
          typeof err.body === 'object' &&
          'lockoutUntil' in err.body
        ) {
          const lo = (err.body as { lockoutUntil: string }).lockoutUntil
          setLockoutUntil(lo)
          setError(`Too many attempts. Try again at ${formatLockoutTime(lo)}.`)
        } else {
          setError(err.friendlyMessage)
        }
      } else {
        setError('Something went wrong. Try again.')
      }
    }
  }

  return (
    <div
      className="paper"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 20px',
      }}
    >
      <div style={{ marginBottom: 28 }}>
        <Wordmark size={36} />
      </div>

      <form
        onSubmit={onSubmit}
        className="felt-card"
        style={{
          width: '100%',
          maxWidth: 360,
          padding: '20px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <div>
          <div className="kicker" style={{ marginBottom: 4 }}>
            sign in
          </div>
          <div
            style={{
              fontFamily: 'var(--font-serif)',
              fontStyle: 'italic',
              fontSize: 22,
              color: 'var(--ink)',
              lineHeight: 1.1,
            }}
          >
            Welcome back.
          </div>
        </div>

        <Field label="Handle">
          <input
            type="text"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="cora"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="username"
            autoFocus
            style={inputStyle}
          />
        </Field>

        <Field label="Code" hint="Two words from kwb, e.g. flying-table">
          <input
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="word-word"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="current-password"
            style={inputStyle}
          />
        </Field>

        {error && (
          <div
            style={{
              fontSize: 13,
              color: 'var(--terra-ink)',
              padding: '6px 8px',
              borderLeft: '2px solid var(--terra-ink)',
              background: 'rgba(194, 99, 69, 0.06)',
            }}
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          className="btn-stamp"
          disabled={signIn.isPending || lockoutUntil !== null}
          style={{ marginTop: 4 }}
        >
          {signIn.isPending ? 'Signing in…' : 'Sign in'}
        </button>

        <div style={{ fontSize: 12, color: 'var(--ink-3)', textAlign: 'center', marginTop: 4 }}>
          Lost your code? Ping kwb for a fresh one.
        </div>
      </form>
    </div>
  )
}

const Field = ({
  label,
  hint,
  children,
}: { label: string; hint?: string; children: React.ReactNode }) => (
  // biome-ignore lint/a11y/noLabelWithoutControl: input is nested via children
  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <span className="kicker">{label}</span>
    {children}
    {hint && <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{hint}</span>}
  </label>
)

const inputStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 16,
  padding: '10px 12px',
  border: '1px solid var(--line-soft)',
  borderRadius: 4,
  background: 'var(--paper)',
  color: 'var(--ink)',
}
